import React, { useMemo, useRef, useState } from 'react';
import { Column, ColumnConfig } from '@ant-design/charts';
import {
    Collapse,
    Typography,
    Flex,
    Button,
    Spin,
    ConfigProvider,
    theme,
} from '@app/ui';
import { useTranslation } from 'react-i18next';
import * as format from '@app/utils/formatters';
import { BroadcastViewership } from '@app/types/api';
import useDebounce from '@app/components/hooks/useDebounce';
import Empty from '@app/components/common/Empty';
import connect from '@app/utils/connect';
import combineActions from '@app/utils/combineActions';
import sendNotification from '@app/actions/notifications/send';
import { Notification } from '@app/components/notifications/Notifications';

interface BroadcastViewershipBarChartProps {
    broadcasts: BroadcastViewership[];
    selectedBroadcastName: string;
    setSelectedBroadcastName: React.Dispatch<React.SetStateAction<string>>;
    onLoadMore: () => void;
    hasNextPage: boolean;
    loading: boolean;
    viewershipType: string;
    sendNotification: (
        message: string,
        config?: Notification['options'],
    ) => void;
}

const BroadcastViewershipBarChart = ({
    broadcasts = [],
    selectedBroadcastName,
    setSelectedBroadcastName,
    onLoadMore,
    hasNextPage,
    loading,
    viewershipType,
    sendNotification,
}: BroadcastViewershipBarChartProps) => {
    const { t } = useTranslation();
    const [activeKey, setActiveKey] = useState<string[] | null>(null);
    const chartRef = useRef<any>(null);

    const chartData = useMemo(() => {
        return broadcasts.map(broadcast => {
            const value =
                viewershipType === 'individual'
                    ? broadcast.individualViewership
                    : broadcast.averageViewership;

            return {
                label: `${format.shortDate(broadcast.contentDate)}\n${format
                    .splitWordsByMaxLineLength(
                        format.trimDate(broadcast.name),
                        24,
                    )
                    .join('\n')}`,
                value,
                name: broadcast.name,
                date: format.shortDate(broadcast.contentDate),
            };
        });
    }, [broadcasts, selectedBroadcastName, viewershipType]);

    const {
        token: { colorPrimary },
    } = theme.useToken();

    const collapseContentRef = useRef<HTMLDivElement>(null);

    const debouncedHeight = useDebounce(
        collapseContentRef.current?.clientHeight,
        100,
    );

    const isCollapseFullyOpen =
        collapseContentRef.current?.clientHeight === debouncedHeight &&
        collapseContentRef.current?.clientHeight > 0;

    const title = t('measurement:broadcastExposure.chart.title', {
        viewershipType: t(
            `measurement:broadcastExposure.viewershipTypes.${viewershipType}`,
        ),
    });

    // English title for PNG filename (always in English)
    const englishTitleForFilename = useMemo(() => {
        const viewershipTypeMap: Record<string, string> = {
            household: 'Household',
            individual: 'Individual',
            average: 'Average',
        };
        const englishViewershipType =
            viewershipTypeMap[viewershipType] || viewershipType;
        return `${englishViewershipType} Viewership by Broadcast`;
    }, [viewershipType]);

    const clickTimeoutRef = useRef(null);

    const chartConfig: ColumnConfig = useMemo(() => {
        return {
            data: chartData,
            xField: 'label',
            yField: 'value',
            isGroup: false,
            animation: false,
            style: {
                fill: ({ name }) => {
                    return name === selectedBroadcastName
                        ? '#2DC850'
                        : colorPrimary;
                },
            },
            interaction: {
                tooltip: {
                    render: (e, { title, items }) => {
                        const [date, ...splitName] = title.split('\n');
                        const name = splitName.join(' ');
                        const { color, value } = items[0];
                        return (
                            <div key={title} style={{ padding: '4px 8px' }}>
                                <div
                                    style={{
                                        fontWeight: 'bold',
                                        marginBottom: 6,
                                        fontSize: 12,
                                        color: '#555',
                                    }}
                                >
                                    {name}
                                </div>
                                <div
                                    style={{
                                        marginBottom: 6,
                                        fontSize: 12,
                                        color: '#555',
                                    }}
                                >
                                    {date}
                                </div>

                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        margin: '4px 0',
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <span
                                            style={{
                                                display: 'inline-block',
                                                width: 6,
                                                height: 6,
                                                borderRadius: '50%',
                                                backgroundColor: color,

                                                marginRight: 6,
                                            }}
                                        />
                                        <span
                                            style={{
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {t(
                                                'measurement:broadcastExposure.chart.tooltipLabel',
                                            )}
                                        </span>
                                    </div>
                                    <b
                                        style={{
                                            marginLeft: 8,
                                        }}
                                    >
                                        {format.abbreviatedDecimal(value)}
                                    </b>
                                </div>
                            </div>
                        );
                    },
                },
            },
            interactions: [{ type: 'element-highlight' }],
            onReady: (chart: any) => {
                chartRef.current = chart;
            },
            onEvent: (_, event) => {
                if (event?.type === 'click') {
                    if (clickTimeoutRef.current) {
                        clearTimeout(clickTimeoutRef.current);
                    }

                    clickTimeoutRef.current = setTimeout(() => {
                        const name = event.data?.data?.name;
                        setSelectedBroadcastName(prev =>
                            name === prev ? null : name,
                        );
                    }, 50);
                }
            },
            title: {
                title,
            },
            axis: {
                x: {
                    labelFontSize: 16,
                    labelFill: '#5E6066',
                    labelFontWeight: 500,
                    labelOpacity: 1,
                    labelTransform: 'rotate(0)',
                },
                y: {
                    grid: true,
                    line: true,
                    tickLine: true,
                    title: t('measurement:broadcastExposure.chart.yAxisTitle'),
                    titleFontSize: 16,
                    titleFill: '#5E6066',
                    titleStroke: 'transparent',
                    labelFormatter: (value: any) =>
                        format.abbreviatedDecimal(
                            typeof value === 'string'
                                ? parseFloat(value)
                                : value,
                        ),
                    labelFontSize: 16,
                    labelFill: '#5E6066',
                    labelFontWeight: 500,
                    labelOpacity: 1,
                },
            },
            legend: false,
        };
    }, [chartData, selectedBroadcastName, isCollapseFullyOpen, t, title]);

    const noVisuals = loading;

    const download = e => {
        try {
            e.stopPropagation();
            e.preventDefault();
            const downloadTitle = englishTitleForFilename.replace(/\s/g, '_');
            const chart = chartRef.current;
            if (chart) {
                chart?.downloadImage(`${downloadTitle}.png`);
                sendNotification(
                    t(
                        'measurement:broadcastExposure.chart.notifications.chartDownloaded',
                    ),
                    {
                        context: 'success',
                    },
                );
            } else {
                sendNotification(
                    t(
                        'measurement:broadcastExposure.chart.notifications.noChart',
                    ),
                    {
                        context: 'info',
                    },
                );
            }
        } catch (e) {
            sendNotification(
                t(
                    'measurement:broadcastExposure.chart.notifications.downloadError',
                ),
                {
                    context: 'error',
                },
            );
        }
    };

    React.useEffect(() => {
        if (!noVisuals) {
            setActiveKey(['1']);
        } else {
            setActiveKey(null);
        }
    }, [noVisuals]);

    return (
        <ConfigProvider
            theme={{
                components: {
                    Collapse: {
                        headerBg: '#FFF',
                        headerPadding: '12px 16px',
                    },
                },
            }}
        >
            <Collapse
                style={{
                    margin: 8,
                }}
                expandIconPosition="end"
                activeKey={activeKey}
                collapsible={noVisuals ? 'disabled' : undefined}
                onChange={keys => setActiveKey(keys as string[])}
                items={[
                    {
                        key: '1',
                        label: (
                            <Flex justify="space-between" align="center">
                                <Typography.Title
                                    level={4}
                                    style={{
                                        margin: 0,
                                        color: !noVisuals
                                            ? '#1f1f1f'
                                            : '#bcbcbc',
                                        fontWeight: 400,
                                    }}
                                >
                                    {t(
                                        'measurement:broadcastExposure.chart.visualization',
                                    )}
                                </Typography.Title>
                                <Button
                                    onClick={download}
                                    className="-m-l-2"
                                    data-test="insights-download"
                                    type="primary"
                                    color="primary"
                                    variant="solid"
                                    disabled={noVisuals}
                                >
                                    <i className="fa-regular fa-download -m-r-1" />
                                    {t(
                                        'measurement:broadcastExposure.chart.downloadPng',
                                    )}
                                </Button>
                            </Flex>
                        ),
                        children: (
                            <Flex
                                ref={collapseContentRef}
                                justify="center"
                                align="center"
                            >
                                <div
                                    style={{ overflowX: 'auto', width: '100%' }}
                                >
                                    <Flex
                                        style={{
                                            minHeight: 400,
                                            minWidth: Math.max(
                                                broadcasts.length * 250,
                                                800,
                                            ),
                                        }}
                                        justify="center"
                                        align="center"
                                    >
                                        {loading ? (
                                            <Flex
                                                justify="center"
                                                align="center"
                                                style={{
                                                    height: '100%',
                                                    width: '100%',
                                                }}
                                            >
                                                <Spin size="large" />
                                            </Flex>
                                        ) : chartData.length > 0 ? (
                                            <Column {...chartConfig} />
                                        ) : (
                                            <Empty />
                                        )}
                                        {hasNextPage && !loading && (
                                            <Flex
                                                justify="center"
                                                align="center"
                                            >
                                                <Button
                                                    type="primary"
                                                    onClick={onLoadMore}
                                                >
                                                    {t(
                                                        'measurement:broadcastExposure.chart.loadMore',
                                                    )}
                                                </Button>
                                            </Flex>
                                        )}
                                    </Flex>
                                </div>
                            </Flex>
                        ),
                    },
                ]}
            />
        </ConfigProvider>
    );
};

export default connect(
    ['user'],
    combineActions({ sendNotification }),
)(BroadcastViewershipBarChart);
